import React from 'react';

import PeopleDelta, { SENSE_MORE_IS_BAD, SENSE_MORE_IS_GOOD, SENSE_NEUTRAL } from 'Components/people/PeopleDelta';

import { decimal } from 'Helpers/currencyFormat';
import { formatSeparateByThousands, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

const COUNT = (value) => formatSeparateByThousands(value);
const MONEY = (value) => formatToFixedMoneyWithLocale(value);

/*
 * FTE keeps two decimals even when the value is whole. `json_encode` drops a zero
 * fraction, so a time-weighted 3.0 arrives as `3`, and every formatService helper
 * round-trips through `Intl.NumberFormat` on a string — which strips the trailing
 * zeros back off, printing "3" beside a sibling delta of "+9.25".
 */
const FTE = (value) => decimal(Number(value), 2);

/**
 * The six KPIs, in display order.
 *
 * There is deliberately no peak-excluding-surge tile: InBudget does not model a
 * surge window yet, so the figure cannot be computed and a placeholder tile would
 * read as a real zero.
 *
 * `money: true` marks a tile the payload OMITS (not nulls) for a user without the
 * pay-rate permission. Those tiles are dropped from the strip entirely — the
 * control rail's lock affordance explains the absence.
 */
const KPIS = [
    { key: 'peak_headcount', label: () => $t('Peak headcount'), format: COUNT, sense: SENSE_NEUTRAL },
    { key: 'positions', label: () => $t('Positions'), format: COUNT, sense: SENSE_NEUTRAL },
    { key: 'fte', label: () => $t('FTE'), format: FTE, sense: SENSE_NEUTRAL },
    { key: 'committed', label: () => $t('Committed'), format: MONEY, sense: SENSE_NEUTRAL, money: true },
    { key: 'behind_schedule', label: () => $t('Behind schedule'), format: COUNT, sense: SENSE_MORE_IS_BAD },
    { key: 'savings_to_date', label: () => $t('Savings to date'), format: MONEY, sense: SENSE_MORE_IS_GOOD },
];

function KpiTile({ title, value, delta, format, sense }) {
    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
            <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{title}</div>
            <div className="tw-text-2.5xl tw-text-charcoal tw-font-semibold">{value}</div>
            {/* inline={false} keeps phase 1's block-level flex exactly — see PeopleDelta. */}
            <PeopleDelta delta={delta} format={format} sense={sense} inline={false} className="tw-mt-1" />
        </div>
    );
}

/**
 * @param {Object} kpiStrip - the payload's `kpi_strip`; money keys are absent for
 *                            users without the pay-rate permission.
 */
export default function PeopleKpiStrip({ kpiStrip }) {
    if (!kpiStrip) {
        return null;
    }

    // `key in obj`, not a truthiness or null check: a genuine 0 must render, and
    // an omitted money key must drop the tile.
    const visible = KPIS.filter((kpi) => kpi.key in kpiStrip);

    return (
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-x-5 tw-gap-y-3">
            {visible.map((kpi) => (
                <KpiTile
                    key={kpi.key}
                    title={kpi.label()}
                    value={kpi.format(kpiStrip[kpi.key])}
                    delta={kpiStrip[`${kpi.key}_delta`]}
                    format={kpi.format}
                    sense={kpi.sense}
                />
            ))}
        </div>
    );
}
